import AsyncStorage from '@react-native-async-storage/async-storage';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

const CONSENT_KEY = 'consent_v1';

/** Banner συγκατάθεσης (cookies/τοπική αποθήκευση) — εμφανίζεται μία φορά. */
export function ConsentBanner() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [visible, setVisible] = useState(false);
  const router = useRouter();
  const insets = useSafeAreaInsets();

  useEffect(() => {
    let mounted = true;
    void AsyncStorage.getItem(CONSENT_KEY).then((v) => {
      if (mounted && !v) setVisible(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (!visible) return null;

  async function accept() {
    try {
      await AsyncStorage.setItem(CONSENT_KEY, '1');
    } catch {
      // αγνόησε — απλώς κλείσε το banner
    }
    setVisible(false);
  }

  return (
    <View style={[styles.wrap, { paddingBottom: insets.bottom + spacing.md }]} pointerEvents="box-none">
      <View style={styles.banner}>
        <Text style={styles.text}>
          {t('consent.text')}{' '}
          <Text
            style={styles.link}
            onPress={() => router.push({ pathname: '/legal', params: { doc: 'privacy' } })}
          >
            {t('consent.privacyLink')}
          </Text>
          .
        </Text>
        <Button title={t('consent.ok')} onPress={accept} />
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    wrap: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      paddingHorizontal: spacing.md,
    },
    banner: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    text: {
      color: colors.text,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    link: {
      color: colors.primaryLight,
      fontWeight: '700',
    },
  });
}
